import type { DisplayTimeZone } from '../api/learner';

/**
 * The one formatter for times on family screens — the parent's and the
 * learner's (UC-P08 BR-P08-04, STU-16-FR-03).
 *
 * Every instant is the server's, and the zone is the one the server chose:
 * the learner's, then the household's, then UTC labelled as UTC. Nothing here
 * reads the browser's zone; every function takes the zone it draws in.
 *
 * Calendar days are handled as `YYYY-MM-DD` keys in that zone. Moving between
 * days, weeks and months is plain date arithmetic on those keys, so a
 * daylight-saving change cannot shift a class onto the wrong day.
 */

/** Used only until the server has said which zone; labelled, like any UTC. */
export const UTC_ZONE: DisplayTimeZone = { zone: 'UTC', source: 'UTC' };

/** DEC-S2-S04-05: a course's meetings, in the IANA zone its timetable is written in. */
export const courseZone = (zone: string): DisplayTimeZone => ({ zone, source: 'COURSE' });

const valid = (iso?: string): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "sábado, 24 de octubre" in the chosen zone. */
export function formatDay(iso: string, tz: DisplayTimeZone): string {
  return new Intl.DateTimeFormat('es', {
    timeZone: tz.zone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(iso));
}

/** "16:00" in the chosen zone (Spanish clocks are 24-hour). */
export function formatTime(iso: string, tz: DisplayTimeZone): string {
  return new Intl.DateTimeFormat('es', {
    timeZone: tz.zone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}

export function formatDayAndTime(iso: string, tz: DisplayTimeZone): string {
  return `${formatDay(iso, tz)}, ${formatTime(iso, tz)}`;
}

/** "sáb, 24 oct, 16:00" — the compact form cards use. "—" when there is no instant. */
export function formatShortDateTime(iso: string | undefined, tz: DisplayTimeZone): string {
  const d = valid(iso);
  if (!d) return iso ?? '—';
  return new Intl.DateTimeFormat('es', {
    timeZone: tz.zone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}

/** "24 de octubre de 2026", with the time when `withTime`. */
export function formatLongDate(
  iso: string | undefined,
  tz: DisplayTimeZone,
  withTime = false,
): string {
  const d = valid(iso);
  if (!d) return iso ?? '—';
  // Date and time are formatted apart and joined here: ICU versions disagree
  // on the joiner ("…, 22:00" vs "… a las 22:00"), so one formatter call
  // would read differently on different machines.
  const date = new Intl.DateTimeFormat('es', {
    timeZone: tz.zone,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
  if (!withTime) return date;
  const time = new Intl.DateTimeFormat('es', {
    timeZone: tz.zone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
  return `${date}, ${time}`;
}

/** The calendar day of an instant in the chosen zone — not the browser's day. */
export function dayKey(iso: string, tz: DisplayTimeZone): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz.zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

/** The sentence a family screen shows once, so nobody guesses whose clock it is. */
export function familyZoneLabel(tz: DisplayTimeZone): string {
  switch (tz.source) {
    case 'LEARNER':
      return `Las horas están en la hora del alumno (${tz.zone}).`;
    case 'HOUSEHOLD':
      return `Las horas están en la hora de tu casa (${tz.zone}).`;
    case 'COURSE':
      return `Las horas están en la hora del curso (${tz.zone}).`;
    default:
      return 'Las horas están en hora UTC.';
  }
}

// ── Calendar days as keys ─────────────────────────────────────────────────────

/** A day key as a UTC-noon Date: only ever used for arithmetic and naming the day. */
const keyDate = (key: string): Date => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
};

const toKey = (d: Date): string => d.toISOString().slice(0, 10);

export function addDays(key: string, days: number): string {
  const d = keyDate(key);
  d.setUTCDate(d.getUTCDate() + days);
  return toKey(d);
}

export function addMonths(key: string, months: number): string {
  const d = keyDate(key);
  return toKey(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1, 12)));
}

/** Monday of the week that holds `key`. */
export function startOfWeek(key: string): string {
  const weekday = (keyDate(key).getUTCDay() + 6) % 7; // Monday = 0
  return addDays(key, -weekday);
}

export function weekDays(key: string): string[] {
  const monday = startOfWeek(key);
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

/** The weeks (Monday to Sunday) that cover the month holding `key`. */
export function monthWeeks(key: string): string[][] {
  const first = `${key.slice(0, 7)}-01`;
  const month = first.slice(0, 7);
  const weeks: string[][] = [];
  let monday = startOfWeek(first);
  while (weeks.length === 0 || monday.slice(0, 7) === month) {
    weeks.push(weekDays(monday));
    monday = addDays(monday, 7);
  }
  return weeks;
}

/** A day key named in Spanish: "lunes, 26 de octubre" (or any subset of parts). */
export function formatDayKey(
  key: string,
  options: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long' },
): string {
  return new Intl.DateTimeFormat('es', { timeZone: 'UTC', ...options }).format(keyDate(key));
}

/** "octubre de 2026". */
export function formatMonthKey(key: string): string {
  return formatDayKey(key, { month: 'long', year: 'numeric' });
}
